import "../dashboard/AdminDashboard.css";
import { Container, Row, Col } from "react-bootstrap";
import { AppLoader } from "../../../Components/common/AppLoader/AppLoader";
import { useNavigate } from "react-router-dom";
import { DoctorPortalProvider, useDoctorPortalCtx } from "../hooks/DoctorPortalContext";
import { AdminSidebar } from "../components/sidebar/AdminSidebar";
import DoctorManagementView from "../screens/doctor-management/DoctorManagementView";
import CreateDoctorProfileScreen from "../screens/doctor-portal/CreateDoctorProfileScreen";
import { PatientQueriesScreen } from "../screens/hospital-portal/HospitalPortalScreens";
import DigitalCmeScreen from "../screens/hospital-portal/DigitalCmeScreen";
import PictureVideoGalleryScreen from "../screens/hospital-portal/PictureVideoGalleryScreen";
import AwardsRecognitionsScreen from "../screens/hospital-portal/AwardsRecognitionsScreen";
import NewsArticlesScreen from "../screens/hospital-portal/NewsArticlesScreen";
import { clearStoredAuth } from "../auth/authStorage";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { useChangePassword } from "../hooks/useChangePassword";

const DoctorDashboardContent = () => {
  const navigate = useNavigate();
  const {
    user,
    currentStep,
    profileView,
    STEPS,
    STEP_DESCRIPTIONS,
    sidebarOpen,
    setSidebarOpen,
    expandedGroups,
    expandedSections,
    toggleMenuGroup,
    toggleMenuSection,
    selectStep,
    doctor,
    degrees,
    specialties,
    medicalProcedures,
    specializations,
    departments,
    designations,
    accreditations,
    establishments,
    userTypes,
    locationTypes,
    locations,
    profileForm,
    profileSaving,
    profileMsg,
    loading,
    openCreateProfile,
    closeCreateProfile,
    handleProfileChange,
    handleSaveProfile,
    MENU_GROUPS,
    mediaGallery,
    mediaGalleryLoading,
    mediaGallerySaving,
    saveMediaGalleryItem,
    deleteMediaGalleryItem,
    awardsRecognition,
    awardsLoading,
    awardsSaving,
    saveAwardRecognition,
    deleteAwardRecognition,
    newsArticles,
    newsArticlesLoading,
    newsArticlesSaving,
    saveNewsArticle,
    deleteNewsArticle,
    digitalCme,
    digitalCmeLoading,
    digitalCmeSaving,
    medicalSpecialties,
    saveDigitalCme,
    deleteDigitalCme,
    updatePortalUser,
  } = useDoctorPortalCtx();

  const {
    showChangePasswordModal,
    changePasswordRequired,
    changePasswordSaving,
    changePasswordError,
    openChangePassword,
    closeChangePassword,
    handleChangePassword,
  } = useChangePassword(user, updatePortalUser);

  const handleLogout = () => {
    clearStoredAuth();
    navigate("/login", { replace: true });
  };

  const renderScreen = () => {
    if (profileView === "create") {
      return (
        <CreateDoctorProfileScreen
          formData={profileForm}
          onChange={handleProfileChange}
          onSave={handleSaveProfile}
          onBack={closeCreateProfile}
          saving={profileSaving}
          savedDoctor={doctor}
          specialties={specialties}
          medicalProcedures={medicalProcedures}
          specializations={specializations}
          departments={departments}
          designations={designations}
          degrees={degrees}
          accreditations={accreditations}
          establishments={establishments}
          userTypes={userTypes}
          locationTypes={locationTypes}
          locations={locations}
        />
      );
    }

    if (loading && currentStep === 0) {
      return <AppLoader message="Loading doctor profile…" variant="portal" />;
    }

    switch (currentStep) {
      case 0:
        return (
          <DoctorManagementView
            doctorRecord={doctor}
            degrees={degrees}
            mediaGallery={mediaGallery}
            awardsRecognition={awardsRecognition}
            newsArticles={newsArticles}
            digitalCme={digitalCme}
            showCreateProfile
            onCreateProfile={openCreateProfile}
          />
        );
      case 1:
        return <PatientQueriesScreen />;
      case 2:
        return (
          <AwardsRecognitionsScreen
            awards={awardsRecognition}
            loading={awardsLoading}
            saving={awardsSaving}
            onSave={saveAwardRecognition}
            onDelete={deleteAwardRecognition}
          />
        );
      case 3:
        return (
          <NewsArticlesScreen
            articles={newsArticles}
            loading={newsArticlesLoading}
            saving={newsArticlesSaving}
            onSave={saveNewsArticle}
            onDelete={deleteNewsArticle}
          />
        );
      case 4:
        return (
          <DigitalCmeScreen
            items={digitalCme}
            medicalSpecialties={medicalSpecialties}
            loading={digitalCmeLoading}
            saving={digitalCmeSaving}
            onSave={saveDigitalCme}
            onDelete={deleteDigitalCme}
          />
        );
      case 5:
        return (
          <PictureVideoGalleryScreen
            mediaGallery={mediaGallery}
            loading={mediaGalleryLoading}
            saving={mediaGallerySaving}
            onSave={saveMediaGalleryItem}
            onDelete={deleteMediaGalleryItem}
          />
        );
      default:
        return null;
    }
  };

  const showPortalHeader = profileView !== "create" && currentStep !== 0;

  return (
    <section className="authdata-section">
      <Container fluid className="authdata-container">
        <Row className="authdata-row">
          <div className="authdata-mobile-bar">
            <button
              className="authdata-hamburger"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
            <span className="authdata-mobile-title">
              {profileView === "create" ? "Create/ Edit Profile" : STEPS[currentStep]}
            </span>
          </div>

          {sidebarOpen && (
            <div className="authdata-overlay" onClick={() => setSidebarOpen(false)} />
          )}

          <AdminSidebar
            sidebarOpen={sidebarOpen}
            currentStep={currentStep}
            expandedGroups={expandedGroups}
            expandedSections={expandedSections}
            toggleMenuGroup={toggleMenuGroup}
            toggleMenuSection={toggleMenuSection}
            selectStep={selectStep}
            menuGroups={MENU_GROUPS}
            brandTitle="Menu Options"
          />

          <Col md={9} lg={10} className="authdata-right">
            <div className={`authdata-form-wrapper authdata-form-wrapper--dm${
              profileView === "create" || currentStep === 5 || currentStep === 2 || currentStep === 3 || currentStep === 4
                ? " authdata-form-wrapper--em"
                : ""
            }`}>
              {profileView !== "create" && (
                <div className="hospital-portal-topbar">
                  <div>
                    {showPortalHeader && (
                      <div className="authdata-form-header">
                        <h3>{STEPS[currentStep]}</h3>
                        <p>{STEP_DESCRIPTIONS[currentStep]}</p>
                      </div>
                    )}
                  </div>
                  <div className="hospital-portal-user">
                    <span className="hospital-portal-user__name">
                      {user?.user_name || user?.mobile_no || "Doctor"}
                    </span>
                    <button
                      type="button"
                      className="hospital-portal-change-password"
                      onClick={openChangePassword}
                    >
                      Change Password
                    </button>
                    <button type="button" className="hospital-portal-logout" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              )}

              {renderScreen()}

              {profileMsg && (
                <div
                  className={`authdata-msg ${profileMsg.startsWith("Error") ? "error" : "success"}`}
                >
                  {profileMsg}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <ChangePasswordModal
        show={showChangePasswordModal}
        required={changePasswordRequired}
        saving={changePasswordSaving}
        error={changePasswordError}
        onClose={closeChangePassword}
        onSubmit={handleChangePassword}
      />
    </section>
  );
};

export const DoctorDashboardPage = () => (
  <DoctorPortalProvider>
    <DoctorDashboardContent />
  </DoctorPortalProvider>
);
